import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Loader2, LogIn } from "lucide-react";
import { isAuthRetryableFetchError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Entrar — Loja EG Elétrica" },
      { name: "description", content: "Acesse sua conta para ver seus pedidos." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [unconfirmedEmail, setUnconfirmedEmail] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setUnconfirmedEmail(false);
    setResendMessage("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) {
      // Falha de transporte/config (ex.: credenciais do Supabase ausentes no
      // navegador) significa que o pedido nem chegou a ser processado — não
      // é um problema de credencial do usuário, então não mostramos a
      // mensagem de "e-mail ou senha inválidos". "email_not_confirmed"
      // também não é isso — a senha está certa, só falta confirmar o e-mail.
      const isUnconfirmed =
        error.code === "email_not_confirmed" || error.message === "Email not confirmed";
      setUnconfirmedEmail(isUnconfirmed);
      setError(
        isAuthRetryableFetchError(error)
          ? "Tivemos um problema técnico e não conseguimos processar seu login agora. Tente novamente em alguns minutos."
          : isUnconfirmed
            ? "Seu e-mail ainda não foi confirmado. Verifique sua caixa de entrada ou reenvie o link abaixo."
            : "E-mail ou senha inválidos.",
      );
      return;
    }
    navigate({ to: "/" });
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage("");
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/confirmar-email` },
    });
    setResending(false);
    setResendMessage(
      error
        ? "Não foi possível reenviar agora. Tente novamente em instantes."
        : "Enviamos um novo e-mail de confirmação.",
    );
  };

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Entrar</h1>
      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-lg bg-white border border-border p-6"
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-eg-dark">E-mail</span>
          <input
            type="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-eg-dark">Senha</span>
          <input
            type="password"
            required
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        {unconfirmedEmail && (
          <div className="text-xs">
            {resendMessage ? (
              <p className="text-eg-goldtext">{resendMessage}</p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="font-medium text-eg-goldtext hover:underline disabled:opacity-60"
              >
                {resending ? "Reenviando..." : "Reenviar e-mail de confirmação"}
              </button>
            )}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-eg-gold px-4 py-2.5 font-semibold text-eg-dark hover:bg-eg-goldhover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <LogIn className="h-5 w-5" />
          )}
          Entrar
        </button>
        <div className="flex justify-between text-xs text-muted-foreground">
          <Link to="/recuperar-senha" className="hover:text-eg-gold transition-colors">
            Esqueci minha senha
          </Link>
          <Link to="/cadastro" className="hover:text-eg-gold transition-colors">
            Criar conta
          </Link>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background: white;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          border-color: var(--eg-gold);
          box-shadow: 0 0 0 2px color-mix(in oklab, var(--eg-gold) 30%, transparent);
        }
      `}</style>
    </div>
  );
}
